import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Car, MapPin, Flag } from "lucide-react";
import { usePrefs } from "@/lib/context";
import { fetcher } from "@/lib/hooks";
import { PageHeader, StatusBadge, Empty } from "@/components/common";
import { LiveMap, isLive, toMapItem, EtaLine } from "@/components/LiveMap";

function TransportRow({ x, selected, onSelect }) {
  const { t, fdatetime } = usePrefs();
  const live = isLive(x.location);
  return (
    <button onClick={onSelect} data-testid={`transport-row-${x.id}`}
      className={`w-full border-b border-slate-100 px-4 py-3 text-start transition-colors hover:bg-slate-50 ${selected ? "bg-[#fdf8ec]" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-bold text-slate-900"><Car size={15} className="text-[#996515]" />{x.number}</span>
        <StatusBadge value={x.status} />
      </div>
      <div className="mt-1 text-xs text-slate-600">{x.assigned_name || "—"} · {fdatetime(x.scheduled_at)}</div>
      <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-500"><MapPin size={11} />{x.pickup || "—"}</div>
      <div className="flex items-center gap-1 text-[11px] text-slate-500"><Flag size={11} />{x.destination || "—"}</div>
      {x.location && <div className="mt-1.5"><EtaLine eta={x.eta} testId={`transport-eta-${x.id}`} /></div>}
      <div className="mt-1.5 flex items-center justify-between text-[11px]">
        {x.location
          ? <span className={`flex items-center gap-1 font-semibold ${live ? "text-emerald-700" : "text-slate-500"}`}>{live && <span className="live-dot" />}{live ? t("enRoute") : `${t("lastUpdate")} ${fdatetime(x.location.at)}`}</span>
          : <span className="text-slate-400">{t("waitingPosition")}</span>}
        <Link to={`/tickets/${x.id}`} onClick={(e) => e.stopPropagation()} className="font-semibold text-[#996515] hover:underline" data-testid={`transport-open-${x.id}`}>{t("view")}</Link>
      </div>
    </button>
  );
}

export default function LiveTracking() {
  const { t } = usePrefs();
  const [sel, setSel] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ["transports-live"], queryFn: () => fetcher("/transports/live"), refetchInterval: 8000 });
  const shown = sel ? data.filter((x) => x.id === sel) : data;
  return (
    <div className="space-y-6" data-testid="live-tracking-page">
      <PageHeader eyebrow={t("concierge")} title={t("liveTracking")} subtitle={t("liveTrackingSub")} />
      {!isLoading && !data.length ? <Empty text={t("noTransports")} /> : (
        <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
          <div className="max-h-[560px] overflow-y-auto rounded-sm border border-slate-200 bg-white" data-testid="transport-list">
            {sel && <button onClick={() => setSel(null)} className="w-full border-b px-4 py-2 text-start text-xs font-semibold text-[#996515]" data-testid="transport-show-all">{t("all")}</button>}
            {data.map((x) => <TransportRow key={x.id} x={x} selected={sel === x.id} onSelect={() => setSel(x.id)} />)}
          </div>
          <LiveMap height="h-[420px] lg:h-[560px]" follow={!!sel} testId="live-tracking-map"
            transports={shown.map((x) => toMapItem(x, `${x.number} · ${x.assigned_name || ""}`))} />
        </div>
      )}
    </div>
  );
}
